import { Element, useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BoxWidthTypes } from "../../../../constants/segmentTools";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import AccordionContainer from "./AccordianContainer";
import AccordionSettings from "./AccordionSettings";
import CustomAccordion from "./CustomAccordion";

export const Accordion = ({
  header,
  expanded,
  margin,
  isApplyMarginOnAllSides,
  allSideMargin,
  borderColor,
  borderWidth,
  borderRadius,
  headerBgColor,
  headerTextColor,
  contentBgColor,
  contentTextColor,
  selectedBoxSizeId,
  dropDownRightIcon,
  headerOpacity,
  contentOpacity,
  dropShadowColor,
  selectedIcon,
}) => {
  const {
    connectors: { connect, drag },
    setProp,
  } = useNode();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const { accordion: accordionProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  useEffect(() => {
    // Apply default props from the reducer if undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Header properties
        if (props.header === undefined) {
          props.header = accordionProps.header;
        }
        
        if (props.expanded === undefined) {
          props.expanded = accordionProps.expanded;
        }
        
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...accordionProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = accordionProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = accordionProps.allSideMargin;
        }
        
        // Border properties
        if (props.borderRadius === undefined) {
          props.borderRadius = accordionProps.borderRadius;
        }
        
        if (props.borderWidth === undefined) {
          props.borderWidth = accordionProps.borderWidth;
        }
        
        if (props.borderColor === undefined) {
          props.borderColor = accordionProps.borderColor || "suggested_color_0";
        }
        
        // Background and text colors
        if (props.headerBgColor === undefined) {
          props.headerBgColor = accordionProps.headerBgColor || "suggested_color_0";
        }
        
        if (props.headerTextColor === undefined) {
          props.headerTextColor = accordionProps.headerTextColor;
        }
        
        if (props.contentBgColor === undefined) {
          props.contentBgColor = accordionProps.contentBgColor;
        }
        
        if (props.contentTextColor === undefined) {
          props.contentTextColor = accordionProps.contentTextColor;
        }
        
        // Other style properties
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = accordionProps.dropShadowColor;
        }
        
        if (props.selectedBoxSizeId === undefined) {
          props.selectedBoxSizeId = accordionProps.selectedBoxSizeId;
        }
        
        if (props.dropDownRightIcon === undefined) {
          props.dropDownRightIcon = accordionProps.dropDownRightIcon;
        }
        
        if (props.headerOpacity === undefined) {
          props.headerOpacity = accordionProps.headerOpacity;
        }
        
        if (props.contentOpacity === undefined) {
          props.contentOpacity = accordionProps.contentOpacity;
        }
        
        if (props.selectedIcon === undefined) {
          props.selectedIcon = accordionProps.selectedIcon;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Accordion);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin.top - 4}px ${margin.right}px ${margin.bottom - 5}px ${
        margin.left
      }px`
    : undefined;

  const getMarginStyle = () => {
    if (selectedBoxSizeId === BoxWidthTypes.ContentWidth.id) {
      return {
        margin: marginStyle,
      };
    } else if (selectedBoxSizeId === BoxWidthTypes.FullWidth.id) {
      return {
        marginTop: isApplyMarginOnAllSides ? allSideMargin : margin.top,
        marginBottom: isApplyMarginOnAllSides ? allSideMargin : margin.bottom,
      };
    }
    return {};
  };

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div ref={(ref) => connect(drag(ref))} style={{ ...getMarginStyle() }}>
      <BlockIndicator>
        <CustomAccordion
          header={header}
          expanded={isExpanded}
          onToggle={handleToggle}
          dropDownRightIcon={dropDownRightIcon}
          borderColor={getPaletteColor(borderColor)}
          borderWidth={borderWidth}
          borderRadius={borderRadius}
          headerBgColor={getPaletteColor(headerBgColor)}
          headerOpacity={headerOpacity}
          dropShadowColor={getPaletteColor(dropShadowColor)}
          isHeaderHtml
        >
          <div
            style={{
              backgroundColor: getPaletteColor(contentBgColor),
              backgroundImage: contentBgColor && contentBgColor.startsWith('linear-gradient')
                ? getPaletteColor(contentBgColor)
                : undefined,
              opacity: contentOpacity,
              position: "relative",
              // padding: "16px",
              wordWrap: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
              borderBottomLeftRadius: isExpanded ? borderRadius : 0,
              borderBottomRightRadius: isExpanded ? borderRadius : 0,
            }}
            onMouseDown={(e) => e.stopPropagation()} // Prevent drag events from bubbling up
          >
            <Element
              id="content"
              is={AccordionContainer}
              canvas
              header={header}
              expanded={isExpanded}
              margin={margin}
              isApplyMarginOnAllSides={isApplyMarginOnAllSides}
              allSideMargin={allSideMargin}
              borderColor={borderColor}
              borderWidth={borderWidth}
              borderRadius={borderRadius}
              headerBgColor={headerBgColor}
              headerTextColor={headerTextColor}
              contentBgColor={getPaletteColor(contentBgColor)}
              contentTextColor={contentTextColor}
              selectedBoxSizeId={selectedBoxSizeId}
              dropDownRightIcon={dropDownRightIcon}
              headerOpacity={headerOpacity}
              contentOpacity={contentOpacity}
            />
          </div>
        </CustomAccordion>
      </BlockIndicator>
    </div>
  );
};

export const AccordionDefaultProps = {
  header: undefined,
  expanded: undefined,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  borderColor: undefined,
  borderWidth: undefined,
  borderRadius: undefined,
  headerBgColor: undefined,
  headerTextColor: undefined,
  contentBgColor: undefined,
  contentTextColor: undefined,
  selectedBoxSizeId: undefined,
  dropDownRightIcon: undefined,
  headerOpacity: undefined,
  contentOpacity: undefined,
  dropShadowColor: undefined,
  selectedIcon: undefined,
};

Accordion.craft = {
  props: AccordionDefaultProps,
  related: {
    settings: AccordionSettings,
  },
  rules: {
    canDrop: (node) => true,
  },
};

export default Accordion;
